"use client";

import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export const REQUIRED_MAPPING_FIELDS = ['assetId', 'name'] as const;

export const OPTIONAL_MAPPING_FIELDS = [
  'instrumentType',
  'location',
  'manufacturer',
  'model',
  'serialNumber',
  'calibrationFrequencyMonths',
  'calibrationIntervalDays',
  'lastCalibrationDate',
  'nextCalibrationDue',
  'notes',
] as const;

export const ALL_MAPPING_FIELDS = [
  ...REQUIRED_MAPPING_FIELDS,
  ...OPTIONAL_MAPPING_FIELDS,
] as const;

export type MappingFieldId = (typeof ALL_MAPPING_FIELDS)[number];
export type MappingSelections = Record<MappingFieldId, string | null>;
export type StoredMapping = Partial<Record<MappingFieldId, string>>;

export type AutoAssignmentMap = Partial<
  Record<MappingFieldId, { header: string; reason: 'stored' | 'alias' }>
>;

export type MappingDialogStrategy = {
  openDialog?: boolean;
  autoOpenOnMissing?: boolean;
  autoConfirmOnSatisfied?: boolean;
};

export const FIELD_ALIAS_MAP: Record<MappingFieldId, string[]> = {
  assetId: [
    'asset id',
    'asset_id',
    'asset code',
    'record id',
    'record_id',
    'id',
    'instrument id',
    'instrument_id',
  ],
  name: ['name', 'instrument name', 'instrument', 'instrument description', 'equipment name'],
  instrumentType: [
    'instrument type',
    'instrument_type',
    'type',
    'calibration type',
    'calibration_type',
    'category',
  ],
  location: ['location', 'calibration location', 'site', 'area', 'plant'],
  manufacturer: ['manufacturer', 'maker', 'brand'],
  model: ['model', 'model number'],
  serialNumber: ['serial number', 'serial_number', 'serial'],
  calibrationFrequencyMonths: [
    'calibration frequency months',
    'calibration_frequency_months',
    'frequency (months)',
    'interval months',
  ],
  calibrationIntervalDays: [
    'calibration interval days',
    'calibration_interval_days',
    'frequency (days)',
    'interval days',
  ],
  lastCalibrationDate: [
    'last calibration date',
    'last_calibration_date',
    'calibration date',
    'previous calibration date',
  ],
  nextCalibrationDue: ['next due date', 'next_due_date', 'due date', 'next calibration due'],
  notes: ['notes', 'comments', 'remarks'],
};

export const FIELD_TO_COLUMN_KEY: Record<MappingFieldId, string> = {
  assetId: 'asset_id',
  name: 'name',
  instrumentType: 'instrument_type',
  location: 'location',
  manufacturer: 'manufacturer',
  model: 'model',
  serialNumber: 'serial_number',
  calibrationFrequencyMonths: 'calibration_frequency_months',
  calibrationIntervalDays: 'calibration_interval_days',
  lastCalibrationDate: 'last_calibration_date',
  nextCalibrationDue: 'next_due_date',
  notes: 'notes',
};

const normalizeForMatch = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\-]+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const createEmptySelections = (): MappingSelections =>
  ALL_MAPPING_FIELDS.reduce(
    (acc, field) => {
      acc[field] = null;
      return acc;
    },
    {} as MappingSelections,
  );

export const shouldOpenMappingDialog = (
  strategy: MappingDialogStrategy,
  missingRequiredCount: number,
): boolean => {
  if (strategy.openDialog) {
    return true;
  }
  if (strategy.autoOpenOnMissing && missingRequiredCount > 0) {
    return true;
  }
  return false;
};

export const shouldAutoConfirmMapping = (
  strategy: MappingDialogStrategy,
  missingRequiredCount: number,
  dialogOpen: boolean,
): boolean => {
  if (dialogOpen) {
    return false;
  }
  if (!strategy.autoConfirmOnSatisfied) {
    return false;
  }
  return missingRequiredCount === 0;
};

export const buildInitialSelections = (
  headers: string[],
  storedMapping?: StoredMapping | null,
): { selections: MappingSelections; autoAssignments: AutoAssignmentMap } => {
  const selections = createEmptySelections();
  const autoAssignments: AutoAssignmentMap = {};
  const usedHeaders = new Set<string>();
  const normalizedHeaders = headers.map((header) => ({
    original: header,
    normalized: normalizeForMatch(header),
  }));

  const assignSelection = (field: MappingFieldId, header: string, reason?: 'stored' | 'alias') => {
    if (usedHeaders.has(header)) {
      return;
    }
    selections[field] = header;
    usedHeaders.add(header);
    if (reason) {
      autoAssignments[field] = { header, reason };
    }
  };

  if (storedMapping) {
    for (const field of ALL_MAPPING_FIELDS) {
      const savedHeader = storedMapping[field];
      if (savedHeader && headers.includes(savedHeader)) {
        assignSelection(field, savedHeader, 'stored');
      }
    }
  }

  const attemptAliasMatch = (field: MappingFieldId) => {
    const spaced = field.replace(/([a-z])([A-Z])/g, '$1 $2');
    const base = [field, spaced, spaced.replace(/\s+/g, '_')];
    const aliases = [...base, ...(FIELD_ALIAS_MAP[field] ?? [])]
      .map(normalizeForMatch)
      .filter(Boolean);

    for (const alias of aliases) {
      const entry = normalizedHeaders.find(
        ({ original, normalized }) => !usedHeaders.has(original) && normalized === alias,
      );
      if (entry) {
        assignSelection(field, entry.original, 'alias');
        return;
      }
    }

    for (const alias of aliases) {
      const entry = normalizedHeaders.find(
        ({ original, normalized }) =>
          !usedHeaders.has(original) && normalized.includes(alias),
      );
      if (entry) {
        assignSelection(field, entry.original, 'alias');
        return;
      }
    }
  };

  for (const field of ALL_MAPPING_FIELDS) {
    if (!selections[field]) {
      attemptAliasMatch(field);
    }
  }

  return { selections, autoAssignments };
};

export const selectionsToColumnMapping = (
  selections: MappingSelections,
): Record<string, string> => {
  const mapping: Record<string, string> = {};
  for (const field of ALL_MAPPING_FIELDS) {
    const header = selections[field];
    if (!header) {
      continue;
    }
    const targetField = FIELD_TO_COLUMN_KEY[field];
    if (targetField) {
      mapping[header] = targetField;
    }
  }
  return mapping;
};

export const loadStoredMapping = (key: string): StoredMapping | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const result: StoredMapping = {};
    for (const field of ALL_MAPPING_FIELDS) {
      const value = (parsed as Record<string, unknown>)[field];
      if (typeof value === 'string') {
        result[field] = value;
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
};

export const persistMapping = (
  key: string,
  selections: MappingSelections,
  remember: boolean,
) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!remember) {
    window.localStorage.removeItem(key);
    return;
  }
  const entries = Object.entries(selections).filter(([, header]) => Boolean(header)) as Array<
    [MappingFieldId, string]
  >;
  if (entries.length === 0) {
    window.localStorage.removeItem(key);
    return;
  }
  const toStore: StoredMapping = {};
  entries.forEach(([field, header]) => {
    toStore[field] = header;
  });
  window.localStorage.setItem(key, JSON.stringify(toStore));
};

export interface CsvPreview {
  headers: string[];
  samples: Record<string, string[]>;
}

export interface TabularData {
  headers: string[];
  rows: Array<Record<string, string | null>>;
}

export const parseTabularFile = async (file: File): Promise<TabularData> => {
  const name = file.name.toLowerCase();

  if (name.endsWith('.xls') || name.endsWith('.xlsx')) {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, { header: 1 }) as Array<
      (string | number | null)[]
    >;

    if (!rows || rows.length < 2) {
      throw new Error('Excel file is empty or has no data rows');
    }

    const headerRow = rows[0] ?? [];
    const headers = headerRow
      .map((cell) => (cell == null ? '' : String(cell).trim()))
      .filter((value) => value.length > 0);

    if (headers.length === 0) {
      throw new Error('We could not detect any headers in that file.');
    }

    const dataRows = rows.slice(1);
    const records = dataRows.map((row) => {
      const record: Record<string, string | null> = {};
      headers.forEach((header, columnIndex) => {
        const value = row?.[columnIndex];
        record[header] = value == null ? null : String(value).trim();
      });
      return record;
    });

    return { headers, rows: records };
  }

  const text = await file.text();
  const parsed = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    skipEmptyLines: true,
  });

  if (parsed.errors && parsed.errors.length > 0) {
    throw new Error(parsed.errors[0]?.message || 'Unable to read CSV data');
  }

  const headers = parsed.meta.fields?.filter((field): field is string => Boolean(field)) ?? [];
  if (headers.length === 0) {
    throw new Error('We could not detect any headers in that file.');
  }

  const records = (parsed.data ?? []).map((row) => {
    const record: Record<string, string | null> = {};
    headers.forEach((header) => {
      const value = row?.[header];
      record[header] = value == null ? null : String(value).trim();
    });
    return record;
  });

  return { headers, rows: records };
};

export const parseCsvPreview = async (file: File, previewRows: number = 20): Promise<CsvPreview> => {
  const name = file.name.toLowerCase();

  if (name.endsWith('.xls') || name.endsWith('.xlsx')) {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, { header: 1 }) as Array<
      (string | number | null)[]
    >;

    if (!rows || rows.length === 0) {
      throw new Error('Unable to read Excel headers');
    }

    const headerRow = rows[0] ?? [];
    const headers = headerRow
      .map((cell) => (cell == null ? '' : String(cell).trim()))
      .filter((value) => value.length > 0);

    if (headers.length === 0) {
      throw new Error('We could not detect any headers in that file.');
    }

    const samples: Record<string, string[]> = {};
    headers.forEach((header) => {
      samples[header] = [];
    });

    for (let rowIndex = 1; rowIndex < rows.length && rowIndex <= previewRows; rowIndex += 1) {
      const row = rows[rowIndex] ?? [];
      headers.forEach((header, columnIndex) => {
        const value = row[columnIndex];
        if (value === undefined || value === null) {
          return;
        }
        const stringValue = String(value).trim();
        if (!stringValue) {
          return;
        }
        const fieldSamples = samples[header];
        if (!fieldSamples.includes(stringValue)) {
          fieldSamples.push(stringValue);
        }
      });
    }

    Object.keys(samples).forEach((field) => {
      samples[field] = samples[field].slice(0, 3);
    });

    return { headers, samples };
  }

  const text = await file.text();
  const preview = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    preview: previewRows,
    skipEmptyLines: true,
  });

  if (preview.errors && preview.errors.length > 0) {
    throw new Error(preview.errors[0]?.message || 'Unable to read CSV headers');
  }

  const fields = preview.meta.fields?.filter((field): field is string => Boolean(field)) ?? [];
  const samples: Record<string, string[]> = {};
  fields.forEach((field) => {
    samples[field] = [];
  });

  preview.data.forEach((row) => {
    fields.forEach((field) => {
      const value = row?.[field];
      if (value === undefined || value === null) {
        return;
      }
      const stringValue = String(value).trim();
      if (stringValue.length === 0) {
        return;
      }
      const fieldSamples = samples[field];
      if (!fieldSamples.includes(stringValue)) {
        fieldSamples.push(stringValue);
      }
    });
  });

  Object.keys(samples).forEach((field) => {
    samples[field] = samples[field].slice(0, 3);
  });

  return { headers: fields, samples };
};
